/**
 * Permissions Configuration
 *
 * Single source of truth for all permission values.
 *
 * Naming Convention: <resource>.<action>
 * Actions: view, manage, full_access
 */

// ==================== Single Source of Truth ====================

export const PERMISSIONS = {
  DASHBOARD: {
    VIEW: "dashboard.view",
    MANAGE: "dashboard.manage",
  },
  REPORTS: {
    VIEW: "reports.view",
    MANAGE: "reports.manage",
  },
  COST_ADVISOR: {
    VIEW: "cost_advisor.view",
    MANAGE: "cost_advisor.manage",
  },
  PERFORMANCE_ADVISOR: {
    VIEW: "performance_advisor.view",
    MANAGE: "performance_advisor.manage",
  },
  SECURITY_ADVISOR: {
    VIEW: "security_advisor.view",
    MANAGE: "security_advisor.manage",
  },
  COST_ALERTS: {
    VIEW: "cost_alerts.view",
    MANAGE: "cost_alerts.manage",
  },
  PERFORMANCE_ALERTS: {
    VIEW: "performance_alerts.view",
    MANAGE: "performance_alerts.manage",
  },
  INSTANCES: {
    VIEW: "instances.view",
    MANAGE: "instances.manage",
  },
  BILLING: {
    VIEW: "billing.view",
    MANAGE: "billing.manage",
  },
  DEPARTMENTS: {
    VIEW: "departments.view",
    MANAGE: "departments.manage",
  },
  MEMBERS: {
    VIEW: "members.view",
    MANAGE: "members.manage",
  },
  ACCOUNT: {
    VIEW: "account.view",
    MANAGE: "account.manage",
  },
  SETTINGS: {
    VIEW: "settings.view",
    MANAGE: "settings.manage",
  },
  GLOBAL: {
    FULL_ACCESS: "*.full_access",
  },
} as const

// ==================== Types ====================

type DeepStringValues<T> = T extends string
  ? T
  : T extends Record<string, unknown>
    ? { [K in keyof T]: DeepStringValues<T[K]> }[keyof T]
    : never

export type RoutePermission = DeepStringValues<typeof PERMISSIONS>

// ==================== Helpers ====================

const P = PERMISSIONS

export const hasPermission = (userPermissions: readonly RoutePermission[], required: RoutePermission): boolean => {
  if (userPermissions.includes(P.GLOBAL.FULL_ACCESS)) return true
  // const resource = required.split(".")[0]
  // if (userPermissions.includes(`${resource}.full_access` as RoutePermission)) return true
  return userPermissions.includes(required)
}

export const hasAnyPermission = (
  userPermissions: readonly RoutePermission[],
  required: readonly RoutePermission[]
): boolean => {
  if (userPermissions.includes(P.GLOBAL.FULL_ACCESS)) return true
  return required.some((p) => hasPermission(userPermissions, p))
}

export const hasAllPermissions = (
  userPermissions: readonly RoutePermission[],
  required: readonly RoutePermission[]
): boolean => {
  if (userPermissions.includes(P.GLOBAL.FULL_ACCESS)) return true
  return required.every((p) => hasPermission(userPermissions, p))
}
